import { type ActionCreatorWithPayload } from "@reduxjs/toolkit";
import { pick } from "lodash";
import { type AppDispatch } from "../../../../Application/globaltypes/redux";

import { type AccountBase, type PackName } from "../../../../../interfaces";
import { TagsEnumCamelPlural } from "../../../../../interfaces/TagsEnum";
import { type GenericFiltersMap, mapFilterOption } from "../../../../../utils/filterUtils";
import { ScormFiltersEnum } from "../../types/models";
import {
  setError as setOverviewError,
  setFilterOptions as setOverviewFilterOptions,
  setIsLoading as setOverviewIsLoading,
} from "../slices/FiltersSlice";

import PromiseStatuses from "../../../../../enums/promiseStatuses";
import objUtils from "../../../../../utils/objectUtils";
import DataService from "../../../../Application/services/dataServices/typedDataService";

export interface GetFilterOptionsProps {
  showPurchased?: boolean;
  filters?: ScormFiltersEnum[];
}

interface BaseFilterOptionsProps {
  begin: () => void;
  success: (filterOptions: GenericFiltersMap<string>) => void;
  failure: (error: Error) => void;
}

const defaultFiltersOptions = [
  ScormFiltersEnum.Publishers,
  ScormFiltersEnum.Tags,
  ScormFiltersEnum.Packs,
  ScormFiltersEnum.Visibility,
];

export const getFilterOptions = async (
  props: BaseFilterOptionsProps & GetFilterOptionsProps,
  includePublishers: boolean = true,
) => {
  const { showPurchased, begin, success, failure } = props;
  const selectedFilters = !props.filters?.length ? defaultFiltersOptions : props.filters;

  let filterOptions: GenericFiltersMap<string> = {};

  const tagToFilterMap: { [key in TagsEnumCamelPlural]?: ScormFiltersEnum } = {
    [TagsEnumCamelPlural.Label]: ScormFiltersEnum.Labels,
    [TagsEnumCamelPlural.SoftwareApplication]: ScormFiltersEnum.SoftwareApplications,
  };

  const readPublishersData = async () => {
    const publishers = await DataService.scorm.getPublishersFilterOptions();
    filterOptions = mapFilterOption(filterOptions, publishers.data, ScormFiltersEnum.Publishers, (item: PackName) => ({
      text: item.name,
      value: item.id,
    }));
  };

  const readPacksData = async () => {
    const packs = await DataService.packs.getPacksFilterOptions("scorm", !!showPurchased);
    filterOptions = mapFilterOption(filterOptions, packs.data, ScormFiltersEnum.Packs, (item: AccountBase) => ({
      text: item.name,
      value: item.id,
    }));
  };

  const readTagsData = async () => {
    const tags = await DataService.scorm.getTagsFilterOptions(!!showPurchased);
    for (const [key, value] of objUtils.typedEntries<TagsEnumCamelPlural, string[]>(tags.data)) {
      if (tagToFilterMap[key]) {
        filterOptions = mapFilterOption(filterOptions, value, tagToFilterMap[key], (item: string) => ({
          text: item,
          value: item,
        }));
      }
    }
  };

  const readVisibilityOptions = async () => {
    filterOptions = mapFilterOption(
      filterOptions,
      [
        { text: "Hidden", value: "false" },
        { text: "Visible", value: "true" },
      ],
      ScormFiltersEnum.Visibility,
      (item) => item,
    );
  };

  const filterHandlers: { [key in ScormFiltersEnum]?: () => Promise<void> } = {
    [ScormFiltersEnum.Packs]: readPacksData,
    [ScormFiltersEnum.Tags]: readTagsData,
    [ScormFiltersEnum.Visibility]: readVisibilityOptions,
    ...(includePublishers && { [ScormFiltersEnum.Publishers]: readPublishersData }),
  };

  begin();

  const filtersHandlersPromises = Object.values(pick(filterHandlers, selectedFilters)).map(
    (handler: () => Promise<void>) => handler(),
  );

  const result = await Promise.allSettled(filtersHandlersPromises);

  let someFulfilled = false;
  result.forEach((item) => {
    ({
      [PromiseStatuses.rejected]: () => failure((item as PromiseRejectedResult).reason),
      [PromiseStatuses.fulfilled]: () => (someFulfilled = true),
    })[item.status]();
  });

  if (someFulfilled) {
    success(filterOptions);
  }
};

const getFilterOptionsWrapper = (
  setIsLoading: ActionCreatorWithPayload<boolean>,
  setFilterOptions: ActionCreatorWithPayload<GenericFiltersMap<string>>,
  setError: ActionCreatorWithPayload<Error>,
) => {
  return (dispatch: AppDispatch) =>
    getFilterOptions({
      showPurchased: true,
      filters: [
        ScormFiltersEnum.Publishers,
        ScormFiltersEnum.Tags,
        ScormFiltersEnum.Packs,
        ScormFiltersEnum.Visibility,
      ],
      begin: () => dispatch(setIsLoading(true)),
      success: (filterOptions: GenericFiltersMap<string>) => {
        dispatch(setFilterOptions(filterOptions));
        dispatch(setIsLoading(false));
      },
      failure: (error: Error) => {
        dispatch(setError(error));
        dispatch(setIsLoading(false));
      },
    });
};

export const getOverviewFilterOptions = () =>
  getFilterOptionsWrapper(setOverviewIsLoading, setOverviewFilterOptions, setOverviewError);
